import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { AlertCircle, CheckCircle2 } from "@/components/icons";
import { supabase } from "@/lib/supabase";
import { stashPendingCheckout } from "@/billing/usePostSignupRedirect";
import { LATEST_LEGAL_VERSION, PENDING_CONSENT_KEY } from "@/legal/version";
import { FormField } from "./FormField";
import { GoogleButton } from "./GoogleButton";
import { ResendVerificationButton } from "./ResendVerificationButton";

export function SignupForm() {
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get("plan");
  const cadenceParam = searchParams.get("cadence");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const { t } = useTranslation("auth");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError(t("signup.passwordsMismatch"));
      return;
    }
    // Stash the consent version BEFORE signUp. The actual POST happens
    // in ConfirmHandler once the user has a JWT (after email-confirmation
    // for email signups, or post-OAuth-redirect for Google). signUp itself
    // does not return a session when email-confirmation is enabled, so
    // calling /me/consent inline here would 401 and silently fail.
    localStorage.setItem(PENDING_CONSENT_KEY, LATEST_LEGAL_VERSION);
    setSubmitting(true);
    const { error: err } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/confirm`,
      },
    });
    setSubmitting(false);
    if (err) {
      setError(err.message);
      return;
    }
    if (
      (planParam === "pro" || planParam === "analyst") &&
      (cadenceParam === "monthly" || cadenceParam === "annual")
    ) {
      stashPendingCheckout({ plan: planParam, cadence: cadenceParam });
    }
    setSubmitted(true);
  }

  if (submitted) {
    return (
      <div>
        <Alert>
          <CheckCircle2 className="h-4 w-4 text-primary" />
          <AlertDescription>
            <Trans
              i18nKey="auth:signup.checkEmail"
              values={{ email }}
              components={{ strong: <strong /> }}
            />
          </AlertDescription>
        </Alert>
        <ResendVerificationButton email={email} />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <FormField
        label={t("signup.email")}
        type="email"
        autoComplete="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <FormField
        label={t("signup.password")}
        type="password"
        autoComplete="new-password"
        required
        minLength={8}
        helperText={t("signup.passwordHelper")}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <FormField
        label={t("signup.confirmPassword")}
        type="password"
        autoComplete="new-password"
        required
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
      />
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          aria-label={t("signup.legalAgreeAria")}
        />
        <span>
          <Trans
            i18nKey="auth:signup.legalAgree"
            components={{
              terms: (
                <a
                  href="https://tapelens.com/legal/terms"
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
              privacy: (
                <a
                  href="https://tapelens.com/legal/privacy"
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          />
        </span>
      </label>
      <Button type="submit" className="w-full" disabled={submitting || !agreed}>
        {submitting ? t("signup.submitting") : t("signup.submit")}
      </Button>
      <div className="relative my-4">
        <Separator />
        <span className="absolute left-1/2 -translate-x-1/2 -top-2.5 bg-card px-2 text-xs text-muted-foreground">
          {t("signup.or")}
        </span>
      </div>
      <GoogleButton disabled={!agreed} />
      <p className="text-sm text-muted-foreground text-center pt-2">
        {t("signup.signinHint")}{" "}
        <Link to="/auth/signin" className="underline hover:text-foreground">
          {t("signup.signinLink")}
        </Link>
      </p>
    </form>
  );
}
